import {ExclamationIcon} from '@primer/octicons-react'
import type {Icon as PrimerIcon} from '@primer/octicons-react'
import {ClockHandsIcon} from '@github-ui/custom-icons/ClockHandsIcon'
import {ForYouChangesRequestedIcon} from '@github-ui/custom-icons/ForYouChangesRequestedIcon'
import {MergeabilityFailureIcon} from '@github-ui/custom-icons/MergeabilityFailureIcon'
import {ForYouItem} from '../shared/ForYouItem'
import type {ForYouItemTone} from '../shared/for-you-item.types'
import {useMergeabilityData} from './use-mergeability-data'
import {decideReviewSubmittedItem, type ReviewSubmittedItemKind} from './shared/review-submitted-item-decider'
import {useUnresolvedConversationCount} from '../../../../stores/layout-selectors'
import {OverviewMergeControl} from './shared/OverviewMergeControl'

type ReviewSubmittedVariantConfig = {
  icon: PrimerIcon
  tone: ForYouItemTone
  glyphSize: 12 | 16 | 24
  title: string
  description: string
  itemName: string
}

const VARIANT_CONFIG: Record<Exclude<ReviewSubmittedItemKind, 'none'>, ReviewSubmittedVariantConfig> = {
  'required-changes-requested': {
    icon: ForYouChangesRequestedIcon,
    tone: 'danger',
    glyphSize: 16,
    title: 'Changes requested',
    description: 'Address the requested changes before merging.',
    itemName: 'required-changes-requested',
  },
  'comments-resolution-required': {
    icon: MergeabilityFailureIcon,
    tone: 'danger',
    glyphSize: 12,
    title: 'Unresolved comments must be resolved',
    description: 'Resolve all comments before merging.',
    itemName: 'comments-resolution-required',
  },
  'approval-required': {
    icon: ClockHandsIcon,
    tone: 'attention',
    glyphSize: 24,
    title: 'Approval required',
    description: 'Request another approving review before this can merge.',
    itemName: 'approval-required',
  },
  'address-comments': {
    icon: ExclamationIcon,
    tone: 'attention',
    glyphSize: 12,
    title: 'Comments to address',
    description: 'Address the open review comments and ask for another review.',
    itemName: 'address-comments',
  },
}

// Author-facing item that summarizes the state after a review has been submitted.
// Renders one of four mutually exclusive states, prioritized by the rubric:
// required-changes-requested > comments-resolution-required > approval-required > address-comments.
export function ReviewSubmitted() {
  const data = useMergeabilityData()
  const unresolvedThreadCount = useUnresolvedConversationCount() ?? 0

  const decision = decideReviewSubmittedItem(data, unresolvedThreadCount > 0)
  if (decision.kind === 'none') return null

  const config = VARIANT_CONFIG[decision.kind]
  return (
    <ForYouItem
      itemId={config.itemName}
      visual={{kind: 'icon', icon: config.icon, tone: config.tone, glyphSize: config.glyphSize}}
      title={config.title}
      subText={config.description}
      actions={[
        {
          id: 'merge-when-ready',
          content: <OverviewMergeControl data={data} itemName={config.itemName} />,
        },
      ]}
    />
  )
}
